import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Badge } from 'primereact/badge';
import { ProgressBar } from 'primereact/progressbar';
import { FileUpload } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Timeline } from 'primereact/timeline';
import { Chip } from 'primereact/chip';
import axiosInstance from '../../config/axios.config';

// Demo data function
const getDemoDocumentChecklist = () => ({
    overall_completion: 40,
    critical_missing: 3,
    total_requirements: 8,
    next_action: 'Submit PAN Card and Salary Slip',
    checklist_groups: {
        immediate_action: [
            {
                id: '1',
                template_code: 'PAN_CARD',
                document_name: 'PAN Card',
                description: 'Permanent Account Number card for identity verification',
                status: 'pending',
                is_mandatory: true,
                completion_percentage: 0,
                collection_instructions: 'Upload clear, colored copy of PAN card',
                accepted_formats: ['pdf', 'jpg', 'png'],
                max_file_size: '2MB',
                due_date: new Date(Date.now() + 24 * 60 * 60 * 1000), // Tomorrow
                is_overdue: false,
                blocks_progress: true,
                collection_priority: 'high',
                tips: ['Use good lighting', 'Ensure card is flat', 'Avoid shadows'],
                has_alternatives: false
            },
            {
                id: '2',
                template_code: 'SALARY_SLIP',
                document_name: 'Latest Salary Slips (3 months)',
                description: 'Upload salary slips for last 3 months',
                status: 'pending',
                is_mandatory: true,
                completion_percentage: 0,
                collection_instructions: 'Upload recent salary slips showing current income',
                accepted_formats: ['pdf', 'jpg'],
                max_file_size: '5MB',
                due_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), // 3 days
                is_overdue: false,
                blocks_progress: true,
                collection_priority: 'high',
                tips: ['Ensure employer letterhead is visible', 'All salary components should be clear']
            }
        ],
        can_submit_later: [
            {
                id: '3',
                template_code: 'EMPLOYMENT_LETTER',
                document_name: 'Employment Verification Letter',
                description: 'Letter from employer confirming employment',
                status: 'pending',
                is_mandatory: false,
                completion_percentage: 0,
                collection_instructions: 'Get official letter from HR department',
                accepted_formats: ['pdf'],
                max_file_size: '3MB',
                due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
                blocks_progress: false,
                collection_priority: 'medium'
            }
        ],
        conditional: [
            {
                id: '4',
                template_code: 'ITR_RETURNS',
                document_name: 'Income Tax Returns',
                description: 'ITR for last 2 years (Required if loan amount > ₹5L)',
                status: 'pending',
                is_mandatory: true,
                condition_explanation: 'Required because loan amount is above ₹5 lakhs',
                completion_percentage: 0,
                accepted_formats: ['pdf'],
                max_file_size: '10MB'
            }
        ],
        alternatives_available: [
            {
                id: '5',
                template_code: 'AADHAR_CARD',
                document_name: 'Aadhaar Card',
                description: 'Unique identification document',
                status: 'pending',
                is_mandatory: true,
                completion_percentage: 0,
                has_alternatives: true,
                alternatives: [
                    { display_name: 'Voter ID', template_code: 'VOTER_ID' },
                    { display_name: 'Passport', template_code: 'PASSPORT' },
                    { display_name: 'Driving License', template_code: 'DRIVING_LICENSE' }
                ],
                accepted_formats: ['pdf', 'jpg', 'png'],
                max_file_size: '2MB'
            }
        ]
    }
});

const SmartDocumentChecklist = ({ applicationId }) => {
    const [checklist, setChecklist] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploadDialog, setUploadDialog] = useState(false);
    const [selectedRequirement, setSelectedRequirement] = useState(null);
    const [alternativeDialog, setAlternativeDialog] = useState(false);
    const [verificationDialog, setVerificationDialog] = useState(false);
    
    const toast = useRef(null);
    const fileUpload = useRef(null);

    useEffect(() => {
        loadDocumentChecklist();
    }, [applicationId]);

    const loadDocumentChecklist = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/loan-applications/${applicationId}/document-checklist`);
            if (response.data.success) {
                setChecklist(response.data.data);
            }
        } catch (error) {
            console.error('Error loading document checklist:', error);
            
            // Show demo checklist for demonstration
            setChecklist(getDemoDocumentChecklist());
        } finally {
            setLoading(false);
        }
    };

    const getStatusSeverity = (status) => {
        const severityMap = {
            'pending': 'warning',
            'submitted': 'info',
            'under_review': 'info',
            'verified': 'success',
            'rejected': 'danger',
            'waived': 'secondary'
        };
        return severityMap[status] || 'info';
    };

    const getPrioritySeverity = (priority) => {
        const severityMap = {
            'high': 'danger',
            'medium': 'warning',
            'low': 'info'
        };
        return severityMap[priority] || 'info';
    };

    const openUploadDialog = (requirement) => {
        setSelectedRequirement(requirement);
        setUploadDialog(true);
    };

    const handleFileUpload = async (event) => {
        try {
            const files = event.files;
            if (files.length === 0) return;

            const formData = new FormData();
            formData.append('document', files[0]);
            formData.append('document_template_id', selectedRequirement.template_id);
            formData.append('document_name', selectedRequirement.document_name);

            const response = await axiosInstance.post(
                `/loan-applications/${applicationId}/documents/upload`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.data.success) {
                toast.current.show({ 
                    severity: 'success', 
                    summary: 'Success', 
                    detail: 'Document uploaded successfully', 
                    life: 3000 
                });
                setUploadDialog(false);
                loadDocumentChecklist();
            }
        } catch (error) {
            console.error('Error uploading document:', error);
            toast.current.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: 'Failed to upload document', 
                life: 5000 
            });
        }
    };

    const DocumentRequirementCard = ({ requirement, showPriority = false }) => (
        <Card className={`mb-3 ${requirement.blocks_progress ? 'border-2 border-red-200' : ''}`}>
            <div className="flex justify-content-between align-items-start">
                <div className="flex-1">
                    <div className="flex align-items-center gap-2 mb-2">
                        <h6 className="m-0">{requirement.document_name}</h6>
                        <Tag value={requirement.status} severity={getStatusSeverity(requirement.status)} />
                        {requirement.is_mandatory && <Badge value="Required" severity="danger" />}
                        {showPriority && <Badge value={requirement.collection_priority} severity={getPrioritySeverity(requirement.collection_priority)} />}
                        {requirement.blocks_progress && <Badge value="Blocking" severity="danger" />}
                    </div>
                    
                    <p className="text-sm text-600 mb-2">{requirement.description}</p>
                    
                    {requirement.collection_instructions && (
                        <div className="text-sm text-500 mb-2">
                            <strong>Instructions:</strong> {requirement.collection_instructions}
                        </div>
                    )}
                    
                    <div className="flex align-items-center gap-3 text-sm text-500">
                        <span>Formats: {requirement.accepted_formats?.join(', ') || 'PDF, JPG, PNG'}</span>
                        <span>Max Size: {requirement.max_file_size}</span>
                        {requirement.due_date && (
                            <span className={requirement.is_overdue ? 'text-red-500 font-bold' : ''}>
                                Due: {new Date(requirement.due_date).toLocaleDateString()}
                            </span>
                        )}
                    </div>

                    {requirement.has_alternatives && (
                        <div className="mt-2">
                            <small className="text-blue-600 cursor-pointer" 
                                   onClick={() => {
                                       setSelectedRequirement(requirement);
                                       setAlternativeDialog(true);
                                   }}>
                                View {requirement.alternatives?.length || 0} alternative documents
                            </small>
                        </div>
                    )}
                </div>
                
                <div className="flex flex-column align-items-end gap-2">
                    <ProgressBar value={requirement.completion_percentage} style={{ width: '100px' }}  aria-label="Progress" />
                    <span className="text-sm">{requirement.completion_percentage}%</span>
                    
                    {requirement.status === 'pending' && (
                        <Button 
                            label="Upload" 
                            icon="pi pi-upload" 
                            className="p-button-sm"
                            onClick={() => openUploadDialog(requirement)}
                        />
                    )}
                    {requirement.status === 'submitted' && (
                        <Button 
                            label="Track" 
                            icon="pi pi-eye" 
                            className="p-button-sm p-button-outlined"
                            onClick={() => {
                                setSelectedRequirement(requirement);
                                setVerificationDialog(true);
                            }}
                        />
                    )}
                </div>
            </div>

            {requirement.tips && requirement.tips.length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 border-round">
                    <small className="text-blue-800">
                        <strong>💡 Tips:</strong>
                        <ul className="mt-1 mb-0">
                            {requirement.tips.map((tip, index) => (
                                <li key={index}>{tip}</li>
                            ))}
                        </ul>
                    </small>
                </div>
            )}
        </Card>
    );

    if (loading) {
        return <div className="text-center p-4">Loading document requirements...</div>;
    }

    if (!checklist) {
        return <div className="text-center p-4 text-500">No document requirements found</div>;
    }

    return (
        <div className="grid">
            <Toast ref={toast} />
            
            <div className="col-12">
                {/* Progress Overview */}
                <Card title="Document Collection Progress">
                    <div className="grid">
                        <div className="col-12 md:col-3">
                            <div className="text-center">
                                <div className="text-4xl font-bold text-blue-500">{checklist.overall_completion}%</div>
                                <div className="text-500">Complete</div>
                            </div>
                        </div>
                        <div className="col-12 md:col-3">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-orange-500">{checklist.critical_missing}</div>
                                <div className="text-500">Critical Missing</div>
                            </div>
                        </div>
                        <div className="col-12 md:col-3">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-500">{checklist.total_requirements}</div>
                                <div className="text-500">Total Requirements</div>
                            </div>
                        </div>
                        <div className="col-12 md:col-3">
                            <div className="text-center">
                                <ProgressBar value={checklist.overall_completion} style={{ width: '100%' }}  aria-label="Progress" />
                                <div className="text-500 mt-1">Overall Progress</div>
                            </div>
                        </div>
                    </div>
                    
                    {checklist.next_action && (
                        <div className="mt-3 p-3 bg-orange-100 border-round">
                            <strong>Next Action:</strong> {checklist.next_action}
                        </div>
                    )}
                </Card>
            </div>

            <div className="col-12">
                <Accordion multiple activeIndex={[0]}>
                    {/* Immediate Action Required */}
                    <AccordionTab 
                        header={
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-exclamation-triangle text-red-500"></i>
                                <span>Immediate Action Required</span>
                                <Badge value={checklist.checklist_groups.immediate_action?.length || 0} severity="danger" />
                            </div>
                        }
                    >
                        {checklist.checklist_groups.immediate_action?.length > 0 ? (
                            checklist.checklist_groups.immediate_action.map((req, index) => (
                                <DocumentRequirementCard key={index} requirement={req} showPriority={true} />
                            ))
                        ) : (
                            <div className="text-center p-4 text-500">
                                <i className="pi pi-check-circle text-green-500 text-4xl mb-3"></i>
                                <div>All critical documents submitted!</div>
                            </div>
                        )}
                    </AccordionTab>

                    {/* Can Submit Later */}
                    <AccordionTab 
                        header={
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-clock text-orange-500"></i>
                                <span>Can Submit Later</span>
                                <Badge value={checklist.checklist_groups.can_submit_later?.length || 0} severity="warning" />
                            </div>
                        }
                    >
                        {checklist.checklist_groups.can_submit_later?.map((req, index) => (
                            <DocumentRequirementCard key={index} requirement={req} />
                        ))}
                    </AccordionTab>

                    {/* Conditional Requirements */}
                    <AccordionTab 
                        header={
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-question-circle text-blue-500"></i>
                                <span>Conditional Requirements</span>
                                <Badge value={checklist.checklist_groups.conditional?.length || 0} severity="info" />
                            </div>
                        }
                    >
                        {checklist.checklist_groups.conditional?.map((req, index) => (
                            <Card key={index} className="mb-3 bg-blue-50">
                                <div className="flex align-items-start gap-3">
                                    <i className="pi pi-info-circle text-blue-500 text-xl"></i>
                                    <div>
                                        <h6 className="m-0 mb-2">{req.document_name}</h6>
                                        <p className="text-sm text-600 mb-2">{req.condition_explanation}</p>
                                        <Chip label="Will be required if conditions are met" className="text-xs" />
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </AccordionTab>

                    {/* Alternative Options */}
                    <AccordionTab 
                        header={
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-copy text-purple-500"></i>
                                <span>Alternative Options Available</span>
                                <Badge value={checklist.checklist_groups.alternatives_available?.length || 0} severity="help" />
                            </div>
                        }
                    >
                        {checklist.checklist_groups.alternatives_available?.map((req, index) => (
                            <DocumentRequirementCard key={index} requirement={req} />
                        ))}
                    </AccordionTab>
                </Accordion>
            </div>

            {/* File Upload Dialog */}
            <Dialog 
                visible={uploadDialog} 
                style={{ width: '600px' }} 
                header={`Upload: ${selectedRequirement?.document_name}`} 
                modal 
                onHide={() => setUploadDialog(false)}
            >
                {selectedRequirement && (
                    <div>
                        <div className="mb-4">
                            <h6>Document Information</h6>
                            <p>{selectedRequirement.description}</p>
                            {selectedRequirement.collection_instructions && (
                                <div className="p-3 bg-blue-50 border-round">
                                    <strong>Instructions:</strong> {selectedRequirement.collection_instructions}
                                </div>
                            )}
                        </div>

                        <FileUpload
                            ref={fileUpload}
                            mode="basic"
                            name="document"
                            accept={selectedRequirement.accepted_formats?.map(f => `.${f}`).join(',')}
                            maxFileSize={selectedRequirement.max_file_size_mb * 1024 * 1024}
                            customUpload
                            uploadHandler={handleFileUpload}
                            chooseLabel="Select Document"
                            className="w-full"
                        />

                        {selectedRequirement.tips && (
                            <div className="mt-3 p-3 bg-yellow-50 border-round">
                                <strong>💡 Tips for best quality:</strong>
                                <ul className="mt-2 mb-0">
                                    {selectedRequirement.tips.map((tip, index) => (
                                        <li key={index} className="text-sm">{tip}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </Dialog>

            {/* Alternative Documents Dialog */}
            <Dialog 
                visible={alternativeDialog} 
                style={{ width: '700px' }} 
                header="Alternative Document Options" 
                modal 
                onHide={() => setAlternativeDialog(false)}
            >
                {selectedRequirement && (
                    <div>
                        <p className="mb-4">
                            Instead of <strong>{selectedRequirement.document_name}</strong>, 
                            you can submit any of these alternative documents:
                        </p>
                        
                        {selectedRequirement.alternatives?.map((alt, index) => (
                            <Card key={index} className="mb-3">
                                <div className="flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="m-0">{alt.display_name}</h6>
                                        <p className="text-sm text-600 mt-1">{alt.description}</p>
                                    </div>
                                    <Button 
                                        label="Select This" 
                                        icon="pi pi-check" 
                                        className="p-button-sm p-button-outlined"
                                        onClick={() => openUploadDialog({
                                            ...selectedRequirement,
                                            document_name: alt.display_name,
                                            template_id: alt.template_code
                                        })}
                                    />
                                </div>
                            </Card>
                        )) || <div className="text-500">No alternatives available</div>}
                    </div>
                )}
            </Dialog>

            {/* Document Verification Status Dialog */}
            <Dialog 
                visible={verificationDialog} 
                style={{ width: '600px' }} 
                header="Document Verification Status" 
                modal 
                onHide={() => setVerificationDialog(false)}
            >
                {selectedRequirement && (
                    <div>
                        <div className="flex align-items-center gap-3 mb-4">
                            <i className="pi pi-file text-4xl text-blue-500"></i>
                            <div>
                                <h5 className="m-0">{selectedRequirement.document_name}</h5>
                                <Tag value={selectedRequirement.status} severity={getStatusSeverity(selectedRequirement.status)} />
                            </div>
                        </div>

                        <Timeline 
                            value={[
                                { status: 'Submitted', date: selectedRequirement.first_submitted_at, icon: 'pi pi-upload' },
                                { status: 'Under Review', date: selectedRequirement.last_reviewed_at, icon: 'pi pi-eye' },
                                { status: 'Verified', date: selectedRequirement.verified_at, icon: 'pi pi-check' },
                            ].filter(item => item.date)} 
                            content={(item) => (
                                <div>
                                    <div className="font-semibold">{item.status}</div>
                                    <div className="text-sm text-500">{new Date(item.date).toLocaleString()}</div>
                                </div>
                            )}
                        />

                        {selectedRequirement.verification_notes && (
                            <div className="mt-4 p-3 bg-gray-50 border-round">
                                <strong>Verification Notes:</strong>
                                <p className="mt-2 mb-0">{selectedRequirement.verification_notes}</p>
                            </div>
                        )}
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default SmartDocumentChecklist;